import { Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { OssSectionPanel } from "../common/OssPanel";
import { riskLevel } from "../../constants/labels";

export const OssQuickActionCard = ({ item, onClick }) => (
  <Paper
    component="button"
    type="button"
    elevation={0}
    onClick={onClick}
    sx={{
      width: "100%",
      height: "100%",
      minHeight: { xs: 104, md: 86, xl: 94 },
      p: { xs: 1.75, md: 1.45 },
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      bgcolor: "#fff",
      cursor: "pointer",
      textAlign: "center",
      transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
      "&:hover": {
        borderColor: "#cbd5e1",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.055)",
        transform: "translateY(-1px)",
      },
      "&:focus-visible": {
        outline: "2px solid #2563eb",
        outlineOffset: 2,
      },
    }}
  >
    <Stack spacing={0.9} alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
      <Box
        sx={{
          width: { xs: 42, md: 36, xl: 40 },
          height: { xs: 42, md: 36, xl: 40 },
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: item.color,
          bgcolor: "#f8fafc",
          border: "1px solid #e5e7eb",
          "& .MuiSvgIcon-root": { display: "block", margin: 0 },
        }}
      >
        {item.icon}
      </Box>
      <Typography sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.2 }}>
        {item.title}
      </Typography>
    </Stack>
  </Paper>
);

export const OssFollowUpPanel = ({ summary, actionItems, onNavigate }) => {
  const rows = [
    { label: "확인 필요", count: summary.checkRequired, color: "#f59e0b", path: "actions" },
    { label: "변경 검토", count: summary.changeReview, color: "#8b5cf6", path: "orders?status=CHANGE_REVIEW" },
    { label: "납기 위험", count: summary.deliveryRisk, color: "#ef4444", path: "orders?riskLevel=DELIVERY_RISK" },
    { label: "계획 미확정", count: summary.planPending, color: "#14b8a6", path: "orders?status=PLAN_REVIEW" },
  ];
  const max = Math.max(1, ...rows.map((row) => Number(row.count || 0)));

  return (
    <OssSectionPanel title="후속 처리 알림">
      <Stack spacing={1.1}>
        {rows.map((row) => (
          <Box
            key={row.label}
            component="button"
            type="button"
            onClick={() => onNavigate(row.path)}
            sx={{ p: 0, border: 0, bgcolor: "transparent", textAlign: "left", cursor: "pointer" }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography sx={{ fontSize: 13, color: "#475569" }}>{row.label}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#475569" }}>{row.count || 0}건</Typography>
            </Stack>
            <Box sx={{ mt: 0.55, height: 8, borderRadius: 8, bgcolor: "#eef2f7", overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${Math.max(4, (Number(row.count || 0) / max) * 100)}%`,
                  height: "100%",
                  borderRadius: 8,
                  bgcolor: row.color,
                }}
              />
            </Box>
          </Box>
        ))}

        {actionItems.slice(0, 2).map((item) => (
          <Box
            key={item.order_line_id}
            component="button"
            type="button"
            onClick={() => onNavigate("actions")}
            sx={{
              mt: 0.4,
              px: 1,
              py: 0.85,
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              bgcolor: "#fff",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 800, color: "#111827" }}>
              {item.order_no}
            </Typography>
            <Typography noWrap sx={{ mt: 0.2, fontSize: 12, color: "#64748b" }}>
              {item.item_name_snapshot}
            </Typography>
          </Box>
        ))}
      </Stack>
    </OssSectionPanel>
  );
};

export const OssRiskDistributionPanel = ({ distribution }) => {
  const total = Object.values(distribution || {}).reduce((sum, value) => sum + Number(value || 0), 0);

  return (
    <OssSectionPanel title="위험 분포">
      <Stack spacing={1.2}>
        <Typography sx={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
          착수 필요일까지 남은 여유일을 기준으로 분류합니다.
        </Typography>
        {Object.entries(riskLevel).map(([key, meta]) => {
          const count = Number(distribution?.[key] || 0);
          const rate = total ? (count / total) * 100 : 0;
          return (
            <Box key={key}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, color: "#475569" }}>{meta.label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>{count}건</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={rate}
                sx={{
                  mt: 0.55,
                  height: 8,
                  borderRadius: 8,
                  bgcolor: "#eef2f7",
                  "& .MuiLinearProgress-bar": { bgcolor: meta.tone, borderRadius: 8 },
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </OssSectionPanel>
  );
};

export const OssWeeklyDuePanel = ({ data }) => {
  const maxWeekly = Math.max(1, ...data.map((item) => Number(item.count || 0)));

  return (
    <OssSectionPanel title="향후 6주 납기량">
      <Stack direction="row" spacing={1.25} alignItems="end" sx={{ flex: 1, minHeight: { xs: 190, lg: 145, xl: 170 } }}>
        {data.map((item, index) => {
          const height = Math.max(30, Math.round((Number(item.count || 0) / maxWeekly) * 120));
          return (
            <Stack key={item.week} spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {item.count}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 104,
                  height,
                  borderRadius: "7px 7px 3px 3px",
                  bgcolor: index === 0 ? "#6c5ce7" : "#e5e7eb",
                }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </OssSectionPanel>
  );
};

export const OssQuickActionsPanel = ({ items, onNavigate }) => (
  <OssSectionPanel title="주요 관리 기능">
    <Box
      sx={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
        gridTemplateRows: { lg: "repeat(1, minmax(0, 1fr))" },
        gap: 1.5,
      }}
    >
      {items.map((item) => (
        <OssQuickActionCard key={item.title} item={item} onClick={() => onNavigate(item.path)} />
      ))}
    </Box>
  </OssSectionPanel>
);
